/**
 * One card on the recommendations board (Figma 4249:6034, 4397:3243, 4588:3653; revision modal 4688:5257).
 *
 * A website-change card shows the proposed change to one page of the business's website, Current beside
 * Proposed, with Accept / Request revision / Reject for approvers, and the website's version history. Any task
 * can carry such a proposal (plan L5.2): Growth Intelligence finds the passage in the business's website as the
 * builder saved it, rewrites it, and writes the prompt for the AI website builder, which makes the change once
 * it's accepted. Preparers can also write a website change by hand. Every card shows its details and history.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useParams } from "react-router";

import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import { shortName } from "@/components/team/roles";
import {
  Button,
  cx,
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  Icon,
  Sheet,
  Spinner,
  useToast,
} from "@/components/ui";
import { missingPermissionReason } from "@/lib/access";
import { identity, portal } from "@/lib/api";
import { config } from "@/lib/config";
import { formatBytes, formatDayMonthYear, initials, timeAgo } from "@/lib/format";
import { growthKey, useGrowthProfile, useGrowthTask, useGrowthWebsite } from "@/lib/growth";
import { ApiError, errorMessage } from "@/lib/http";
import type {
  GrowthDraft,
  GrowthSeverity,
  GrowthTaskChange,
  GrowthTaskDetail,
  GrowthTaskStatus,
  GrowthWebsite,
  Member,
  User,
} from "@/lib/types";

import { PHASES } from "./dashboard/board";
import { insightPath, linkText, RECOMMENDATIONS_PATH, WidgetError, WidgetLoading } from "./dashboard/ui";
import { MoveDialog } from "./move-dialog";
import { moveFor, type Mover } from "./pipeline";
import { reasonLabel, RevisionDialog } from "./revision-dialog";
import { control, ghostButton, label, primaryButton } from "./task-dialog";

/** The breadcrumb's last step: where the card is in the pipeline. */
const STATUS_LABEL: Record<GrowthTaskStatus, string> = {
  todo: "To do",
  in_review: "In review",
  needs_approval: "Awaiting approval",
  live: "Live",
  on_hold: "On hold",
};

const PRIORITY: Record<GrowthSeverity, { label: string; tone: string }> = {
  high: { label: "High", tone: "bg-[#d07100]/15 text-[#d07100]" },
  medium: { label: "Medium", tone: "bg-[#12a97e]/15 text-[#12a97e]" },
  low: { label: "Low", tone: "bg-[#faf6ef] text-fg-subtle" },
};

const card = "rounded-2xl border border-border bg-surface shadow-[0px_2px_4px_0px_rgba(112,71,46,0.06)]";
const pillGhost =
  "inline-flex h-10 items-center justify-center rounded-full border-[1.5px] border-[#d4cac4] bg-white px-[22px] text-sm font-semibold text-[#5c5551] transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50";
const pillAction =
  "inline-flex h-11 items-center justify-center rounded-full px-5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60";

/** Starting, preparing and submitting a card: a role that prepares changes, or being assigned to it. */
const PREPARE_REASON = "Needs the Website Manager role, or to be assigned to this task. Ask an owner.";

/** "brightpathstudy.co.uk/applicants": an address as people read it. */
function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname === "/" && !u.hash ? "" : u.pathname}${u.hash}`;
  } catch {
    return url;
  }
}

const isWebAddress = (url: string) => /^https?:\/\//i.test(url);

export function TaskDetailPage() {
  const { taskId = "" } = useParams();
  const profile = useGrowthProfile();
  if (profile.isPending) return <FullPageSpinner />;
  if (profile.isError) return <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />;
  if (profile.data.status !== "active") return <Navigate to="/growth-intelligence" replace />;
  return <TaskDetail taskId={taskId} />;
}

function TaskDetail({ taskId }: { taskId: string }) {
  const task = useGrowthTask(taskId);

  if (task.isPending) return <WidgetLoading label="Loading the task" />;
  if (task.isError) {
    const gone = task.error instanceof ApiError && task.error.status === 404;
    return (
      <div className="flex flex-col gap-5">
        <Crumbs current="Task" />
        <div className="flex flex-col items-center gap-2 rounded-[14px] border border-dashed border-border px-6 py-12 text-center">
          <h1 className="text-sm font-semibold text-fg">
            {gone ? "That task isn't on your board" : "We couldn't load this task"}
          </h1>
          <p className="max-w-md text-[13px] text-fg-muted">
            {gone ? "It may belong to another business." : errorMessage(task.error)}
          </p>
          <Link to={RECOMMENDATIONS_PATH} className={cx(linkText, "mt-1")}>
            Back to the board
          </Link>
        </div>
      </div>
    );
  }
  return <Detail task={task.data} />;
}

function Crumbs({ current }: { current: string }) {
  return (
    <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-[13px]">
      <Link to="/growth-intelligence" className="flex items-center gap-1.5 text-fg-subtle hover:text-fg">
        <Icon name="chevron-left" size={16} />
        Growth Intelligence
      </Link>
      <span aria-hidden className="text-[#d4cac4]">
        /
      </span>
      <Link to={RECOMMENDATIONS_PATH} className="text-fg-subtle hover:text-fg">
        Recommendations
      </Link>
      <span aria-hidden className="text-[#d4cac4]">
        /
      </span>
      <span aria-current="page" className="font-semibold text-fg">
        {current}
      </span>
    </nav>
  );
}

function useMembers() {
  const { orgId } = useOrganization();
  const members = useQuery({ queryKey: ["members", orgId], queryFn: () => identity.members(orgId) });
  return members.data?.items ?? [];
}

const nameOf = (u: User | undefined) =>
  u ? shortName(u.display_name ?? "", u.email) : "Someone in the business";

function Detail({ task }: { task: GrowthTaskDetail }) {
  const { orgId, can } = useOrganization();
  const user = useUser();
  const qc = useQueryClient();
  const toast = useToast();
  const members = useMembers();
  const people = new Map(members.map((m) => [m.user.id, m.user]));
  const who: Mover = { userId: user.id, can };
  const [asking, setAsking] = useState<"accept" | "revise" | "reject" | null>(null);

  const move = useMutation({
    mutationFn: (v: { to: GrowthTaskStatus; message?: string; reason?: string; reasons?: string[] }) =>
      portal.moveTask(orgId, task.id, v.to, {
        version: task.version,
        message: v.message,
        reason: v.reason,
        reasons: v.reasons,
      }),
    onSuccess: (saved, v) => {
      setAsking(null);
      const said: Partial<Record<GrowthTaskStatus, [string, string]>> = {
        live:
          saved.status === "needs_approval"
            ? [
                "Approved",
                `Publish your website to resolve “${task.title}”. The website builder is making the change in a new tab.`,
              ]
            : awaitingPublish
              ? ["Live", `Your website is published with the change: “${task.title}” is Live.`]
              : ["Accepted", `“${task.title}” is approved and Live on the board.`],
        on_hold: [
          task.status === "needs_approval" ? "Rejected" : "Put on hold",
          `“${task.title}” is On hold.`,
        ],
        in_review:
          task.status === "needs_approval"
            ? ["Revision requested", `“${task.title}” is back in review with your note.`]
            : ["Started", `“${task.title}” is in review.`],
        needs_approval: ["Sent for approval", `“${task.title}” is waiting for approval.`],
        todo: ["Back to To do", `“${task.title}” is in To do.`],
      };
      const [title, body] = said[v.to] ?? ["Moved", ""];
      toast.show({ id: `moved-${task.id}`, tone: "success", title, body });
    },
    onError: (e) =>
      toast.show({
        id: `moved-${task.id}`,
        tone: "warning",
        title: "That didn't work",
        body: errorMessage(e),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: growthKey(orgId) }),
  });

  const draft = task.draft;
  const website = task.kind === "website_change";
  const proposal = draft && draft.status !== "superseded" ? draft : null;
  const assigned = task.assignee_user_ids.includes(user.id);
  const mayPrepare = can("growth:drafts_manage") || can("growth:approve") || (assigned && can("growth:use"));
  // a proposal from the saved website, whenever there's nothing being proposed or written (L5.2)
  const proposable =
    (task.status === "todo" || task.status === "in_review" || task.status === "needs_approval") &&
    (!draft || ["failed", "rejected", "revision_requested", "superseded"].includes(draft.status));
  const mayPropose = mayPrepare && proposable;
  const canEditDraft =
    website &&
    (task.status === "todo" || task.status === "in_review") &&
    draft?.status !== "drafting" &&
    (can("growth:drafts_manage") || can("growth:approve") || (assigned && can("growth:use")));
  // approved, and the website builder makes it: the ticket goes Live once the website is published with it
  const awaitingPublish =
    task.status === "needs_approval" &&
    proposal?.status === "accepted" &&
    Boolean(proposal.builder_prompt?.trim());
  const decide = task.status === "needs_approval" && !awaitingPublish;
  const mayAccept = decide && moveFor(task, "live", who) === "approve";
  const mayRevise = decide && moveFor(task, "in_review", who) === "revise";
  const mayReject = decide && moveFor(task, "on_hold", who) === "reject";
  const mayStart = moveFor(task, "in_review", who) === "start";
  const maySubmit = moveFor(task, "needs_approval", who) === "submit";
  const submitReady = !website || (draft?.status === "draft" && draft.proposed_text.trim() !== "");
  // In review opens on the proposed change: a card about the website that got there without one (started before
  // the website was saved, say) asks Growth Intelligence for it once. Without a saved website, the form shows.
  const wantsProposal =
    mayPropose && task.status === "in_review" && !draft && (website || task.insight !== null);
  const autoPropose = useMutation({
    mutationFn: () => portal.requestAiDraft(orgId, task.id, {}),
    onSettled: () => qc.invalidateQueries({ queryKey: growthKey(orgId) }),
  });
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (!wantsProposal || asked.current === task.id) return;
    asked.current = task.id;
    autoPropose.mutate();
  }, [wantsProposal, task.id, autoPropose]);
  const proposing = wantsProposal && !autoPropose.isError;
  // Accept opens the website builder on the change's prompt; the person checks the change there and publishes it.
  // While it waits, the website's versions are read again, and the ticket resolves itself once one is published.
  const site = useGrowthWebsite({ poll: awaitingPublish });
  const siteId = site.data?.site?.id;
  // the version the website becomes once the change is published: the builder's Publish button names it
  const nextVersion = (site.data?.versions ?? []).reduce((n, v) => Math.max(n, v.number), 0) + 1;
  const builderLink =
    siteId && proposal?.builder_prompt
      ? `${config.builderUrl}/sites/${encodeURIComponent(siteId)}?task=${encodeURIComponent(task.id)}&version=${nextVersion}`
      : null;
  const approvedAt = awaitingPublish && proposal?.decided_at ? Date.parse(proposal.decided_at) : null;
  const published =
    approvedAt === null
      ? undefined
      : site.data?.versions.find((v) => v.status === "ready" && Date.parse(v.created_at) >= approvedAt);
  const resolved = useRef<string | null>(null);
  useEffect(() => {
    if (!published || resolved.current === published.deployment_id) return;
    resolved.current = published.deployment_id;
    move.mutate({ to: "live" });
  }, [published, move]);

  return (
    <div className="flex flex-col gap-5">
      <Crumbs current={STATUS_LABEL[task.status]} />

      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-fg">{task.title}</h1>
        <p className="text-[13.5px] leading-[1.8] text-fg-subtle">
          {proposal && isWebAddress(proposal.page_url) ? (
            <>
              Proposed change to{" "}
              <a
                href={proposal.page_url}
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-fg"
              >
                {shortUrl(proposal.page_url)}
              </a>
            </>
          ) : website ? (
            "Website change · no proposed change yet"
          ) : (
            "Task"
          )}
          {task.insight && (
            <>
              {" · based on "}
              <Link to={insightPath(task.insight.id)} className="hover:text-fg">
                “{task.insight.title}”
              </Link>
            </>
          )}
        </p>
      </div>

      {proposal && <Compare draft={proposal} />}
      {proposal && (proposal.builder_prompt || proposal.target_path) && (
        <BuilderPrompt task={task} draft={proposal} mayEdit={mayPrepare} />
      )}
      {draft && <DraftState draft={draft} people={people} />}

      {proposing ? (
        <ReadingWebsite />
      ) : (
        <>
          {proposable && (
            <ProposeChange
              task={task}
              retry={draft !== null}
              blockedReason={mayPrepare ? undefined : PREPARE_REASON}
            />
          )}
          {canEditDraft && <DraftEditor task={task} />}
        </>
      )}

      <div className="flex flex-wrap items-center gap-2.5">
        {decide && (
          <Button
            unstyled
            onClick={() => setAsking("accept")}
            loading={move.isPending}
            disabledReason={
              !mayAccept
                ? missingPermissionReason("growth:approve")
                : website && !submitReady
                  ? "There's no saved proposed change to accept yet."
                  : undefined
            }
            className={cx(pillAction, "bg-[#12a97e] text-white hover:bg-[#0f8f6a]")}
          >
            Accept
          </Button>
        )}
        {decide && (
          <Button
            unstyled
            onClick={() => setAsking("revise")}
            loading={move.isPending}
            disabledReason={mayRevise ? undefined : missingPermissionReason("growth:drafts_manage")}
            className={cx(pillAction, "border-[1.5px] border-[#d4cac4] text-[#5c5551] hover:bg-muted")}
          >
            Request revision
          </Button>
        )}
        {decide && (
          <Button
            unstyled
            onClick={() => setAsking("reject")}
            loading={move.isPending}
            disabledReason={mayReject ? undefined : missingPermissionReason("growth:approve")}
            className={cx(pillAction, "border-[1.5px] border-[#d4cac4] text-[#5c5551] hover:bg-muted")}
          >
            Reject
          </Button>
        )}
        {task.status === "in_review" && (
          <Button
            unstyled
            onClick={() => move.mutate({ to: "needs_approval" })}
            loading={move.isPending}
            disabledReason={
              !maySubmit ? PREPARE_REASON : !submitReady ? "Save the proposed change first." : undefined
            }
            className={cx(pillAction, "bg-brand-500 text-white hover:bg-brand-600")}
          >
            Send for approval
          </Button>
        )}
        {task.status === "todo" && (
          <Button
            unstyled
            onClick={() => move.mutate({ to: "in_review" })}
            loading={move.isPending}
            disabledReason={mayStart ? undefined : PREPARE_REASON}
            className={cx(pillAction, "border-[1.5px] border-[#d4cac4] text-[#5c5551] hover:bg-muted")}
          >
            Start work
          </Button>
        )}
      </div>
      {decide && !mayAccept && <ApprovalNote members={members} />}
      {awaitingPublish && (
        <AwaitingPublish
          builderLink={builderLink}
          published={published?.number}
          busy={move.isPending}
          onCheck={() => move.mutate({ to: "live" })}
        />
      )}

      {task.status === "live" && (task.rolled_back || proposal?.status === "accepted") && (
        <UndoChange task={task} />
      )}
      {(website || proposal) && <VersionHistory task={task} />}

      <TaskFacts task={task} people={people} />
      <Activity history={task.history} people={people} />

      {asking === "revise" && (
        <RevisionDialog
          busy={move.isPending}
          onCancel={() => setAsking(null)}
          onSend={(message, reasons) => move.mutate({ to: "in_review", message, reasons })}
        />
      )}
      {(asking === "accept" || asking === "reject") && (
        <MoveDialog
          task={task}
          kind={asking === "accept" ? "approve" : "reject"}
          body={
            asking === "accept" && builderLink
              ? "It's approved, and the website builder opens in a new tab with this change's prompt. Check the change there and publish your website: the ticket moves to Live once it's published, and can be undone from here afterwards."
              : undefined
          }
          onCancel={() => setAsking(null)}
          onConfirm={(note) => {
            if (asking === "reject") {
              move.mutate({ to: "on_hold", reason: note });
              return;
            }
            // The tab opens in the click itself, so the browser doesn't block it; it goes to the builder once the
            // approval is saved (the builder reads the accepted change), and closes again if it isn't.
            const tab = builderLink ? window.open("", "_blank") : null;
            if (tab) tab.opener = null;
            move.mutate(
              { to: "live" },
              {
                onSuccess: () => {
                  if (tab && builderLink) tab.location.href = builderLink;
                },
                onError: () => tab?.close(),
              },
            );
          }}
        />
      )}
    </div>
  );
}

// ── the proposed change ──────────────────────────────────────────────────────

/**
 * An approved change the website builder makes, waiting for the website to be published with it: the ticket
 * goes Live once it is (the page notices by itself; "Check again" asks at once).
 */
function AwaitingPublish({
  builderLink,
  published,
  busy,
  onCheck,
}: {
  builderLink: string | null;
  /** The version published since the approval, once there is one. */
  published: number | undefined;
  busy: boolean;
  onCheck: () => void;
}) {
  return (
    <section
      aria-live="polite"
      className={cx(
        card,
        "flex flex-wrap items-center gap-4 border-brand-100 bg-brand-50 px-[22px] py-[18px]",
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h2 className="text-[15px] font-semibold text-fg">
          {published ? `Published as v${published}` : "Publish your website to resolve this ticket"}
        </h2>
        <p className="text-[13px] text-fg-muted">
          {published
            ? "Moving the ticket to Live…"
            : "The change is approved. Check it in the website builder and publish your website: the ticket moves to Live once it's published."}
        </p>
      </div>
      {!published && (
        <div className="flex flex-wrap gap-2.5">
          {builderLink && (
            <a
              href={builderLink}
              target="_blank"
              rel="noreferrer"
              className={cx(pillAction, "bg-brand-500 text-white hover:bg-brand-600")}
            >
              Open in website builder
            </a>
          )}
          <button
            type="button"
            onClick={onCheck}
            disabled={busy}
            aria-busy={busy || undefined}
            className={pillGhost}
          >
            {busy ? <Spinner /> : "I've published it"}
          </button>
        </div>
      )}
    </section>
  );
}

/** While Growth Intelligence is asked for the card's change: it reads the website, then the change appears. */
function ReadingWebsite() {
  return (
    <section
      aria-live="polite"
      className={cx(card, "flex items-center gap-3 px-[22px] py-[18px] text-[13.5px] text-fg-muted")}
    >
      <Spinner />
      Reading your website to propose this change…
    </section>
  );
}

/** Ask Growth Intelligence to find this task's change in the business's saved website (plan L5.2). */
function ProposeChange({
  task,
  retry,
  blockedReason,
}: {
  task: GrowthTaskDetail;
  retry: boolean;
  /** Why this person can't ask for it (their role), or undefined when they can. */
  blockedReason?: string;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const ask = useMutation({
    mutationFn: () => portal.requestAiDraft(orgId, task.id, {}),
    onSuccess: () =>
      toast.show({
        id: `draft-${task.id}`,
        tone: "info",
        title: "Reading your website",
        body: "The current wording and the proposed change appear here in a moment.",
      }),
    onError: (e) =>
      toast.show({
        id: `draft-${task.id}`,
        tone: "warning",
        title:
          e instanceof ApiError && e.reason === "WEBSITE_NOT_SAVED"
            ? "Your website isn't saved yet"
            : "That didn't work",
        body: errorMessage(e),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: growthKey(orgId) }),
  });
  return (
    <section className={cx(card, "flex flex-wrap items-center gap-4 px-[22px] py-[18px]")}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h2 className="text-[15px] font-semibold text-fg">
          {retry ? "Propose the change again" : "See how this changes your website"}
        </h2>
        <p className="text-[13px] text-fg-muted">
          Growth Intelligence reads your website, shows what it says now beside what it could say, and writes
          the prompt for the AI website builder.
        </p>
      </div>
      <Button
        unstyled
        onClick={() => ask.mutate()}
        loading={ask.isPending}
        disabledReason={blockedReason}
        className={cx(pillAction, "bg-brand-500 text-white hover:bg-brand-600")}
      >
        {ask.isPending ? <Spinner /> : <Icon name="zap" size={16} className="mr-2" />}
        Propose a website change
      </Button>
    </section>
  );
}

/**
 * The prompt for the AI website builder, which makes the accepted change. It can be edited while the task is
 * being prepared; once accepted, the website builder opens on the business's website to apply it.
 */
function BuilderPrompt({
  task,
  draft,
  mayEdit,
}: {
  task: GrowthTaskDetail;
  draft: GrowthDraft;
  mayEdit: boolean;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const website = useGrowthWebsite();
  const ids = useId();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(draft.builder_prompt ?? "");
  const [copied, setCopied] = useState(false);
  const drafting = draft.status === "drafting";
  const editable = draft.status === "draft" && (task.status === "todo" || task.status === "in_review");
  const siteId = website.data?.site?.id;
  const save = useMutation({
    mutationFn: () =>
      portal.saveDraft(orgId, task.id, {
        page_url: draft.page_url,
        current_text: draft.current_text,
        proposed_text: draft.proposed_text,
        summary: draft.summary,
        version: draft.version,
        builder_prompt: text.trim(),
      }),
    onSuccess: () => {
      toast.show({ id: `prompt-${task.id}`, tone: "success", title: "Builder prompt saved" });
      setEditing(false);
    },
    onError: (e) =>
      toast.show({
        id: `prompt-${task.id}`,
        tone: "warning",
        title: "That didn't work",
        body: errorMessage(e),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: growthKey(orgId) }),
  });
  const copy = () =>
    void navigator.clipboard?.writeText(draft.builder_prompt ?? "").then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });

  return (
    <section aria-labelledby={`${ids}-h`} className={cx(card, "flex flex-col overflow-hidden")}>
      <div className="flex flex-wrap items-center gap-2 bg-brand-50 px-4 py-3">
        <span className="rounded-full bg-[#fde3dc] px-[9px] py-1 text-[11px] font-semibold text-brand-700">
          AI WEBSITE BUILDER
        </span>
        <h2 id={`${ids}-h`} className="text-[13px] font-semibold text-fg">
          The prompt that makes this change
        </h2>
        {draft.target_path && (
          <span className="ml-auto font-mono text-[11.5px] text-fg-subtle">{draft.target_path}</span>
        )}
      </div>
      <div className="flex flex-col gap-4 p-6">
        {drafting ? (
          <p className="flex items-center gap-2 text-[13px] text-fg-muted">
            <Spinner /> Writing the prompt…
          </p>
        ) : editing ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${ids}-prompt`} className={label}>
              Prompt for the website builder
            </label>
            <textarea
              id={`${ids}-prompt`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={2000}
              rows={4}
              className={cx(control, "resize-y")}
            />
          </div>
        ) : (
          <p className="rounded-[10px] bg-[#f8f3f0] px-4 py-3.5 text-[13px] leading-[1.7] whitespace-pre-wrap text-fg">
            {draft.builder_prompt}
          </p>
        )}
        {draft.status === "accepted" && (
          <p className="text-[13px] text-fg-muted">
            Accepted. The website builder makes this change from this prompt: check it there and publish it.
            If its tab didn't open, use Open in website builder.
          </p>
        )}
        {!drafting && (
          <div className="flex flex-wrap gap-2.5">
            {editing ? (
              <>
                <Button
                  unstyled
                  onClick={() => save.mutate()}
                  loading={save.isPending}
                  disabledReason={text.trim() === "" ? "Write the prompt first." : undefined}
                  className={cx(pillAction, "bg-brand-500 text-white hover:bg-brand-600")}
                >
                  Save prompt
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setText(draft.builder_prompt ?? "");
                    setEditing(false);
                  }}
                  className={pillGhost}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={copy} className={pillGhost}>
                  {copied ? "Copied" : "Copy prompt"}
                </button>
                {editable && (
                  <Button
                    unstyled
                    onClick={() => setEditing(true)}
                    disabledReason={mayEdit ? undefined : PREPARE_REASON}
                    className={pillGhost}
                  >
                    Edit prompt
                  </Button>
                )}
                {draft.status === "accepted" && siteId && task.status !== "needs_approval" && (
                  <a
                    href={`${config.builderUrl}/sites/${encodeURIComponent(siteId)}?task=${encodeURIComponent(task.id)}`}
                    target="_blank"
                    rel="noreferrer"
                    className={cx(pillAction, "bg-brand-500 text-white hover:bg-brand-600")}
                  >
                    Open in website builder
                  </a>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function Compare({ draft }: { draft: GrowthDraft }) {
  const drafting = draft.status === "drafting";
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section aria-label="Current" className={cx(card, "flex flex-col overflow-hidden")}>
        <div className="bg-brand-50 px-4 py-3">
          <span className="rounded-full bg-[#f3ebe7] px-[9px] py-1 text-[11px] font-semibold text-[#5c5551]">
            CURRENT
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-6 p-6">
          {draft.current_text ? (
            <p className="rounded-[10px] bg-[#f3ebe7] px-4 py-4 text-[12.5px] text-[#5c5551]">
              “{draft.current_text}”
            </p>
          ) : drafting ? (
            // Growth Intelligence is still finding the passage in the website
            <div role="status" className="flex flex-col gap-2.5 rounded-[10px] bg-[#f3ebe7] px-4 py-4">
              <span className="sr-only">Finding the current wording on your website…</span>
              <span aria-hidden className="h-2.5 w-[85%] animate-pulse rounded bg-[#e3d8d2]" />
              <span aria-hidden className="h-2.5 w-[60%] animate-pulse rounded bg-[#e3d8d2]" />
            </div>
          ) : (
            <p className="rounded-[10px] bg-[#f3ebe7] px-4 py-4 text-[12.5px] text-fg-subtle">
              No current wording: this is new to the page.
            </p>
          )}
          <div>
            {isWebAddress(draft.page_url) ? (
              <a href={draft.page_url} target="_blank" rel="noreferrer" className={pillGhost}>
                Preview
              </a>
            ) : (
              <Button unstyled disabledReason="This page has no web address to open." className={pillGhost}>
                Preview
              </Button>
            )}
          </div>
        </div>
      </section>
      <section aria-label="Proposed" className={cx(card, "flex flex-col overflow-hidden")}>
        <div className="bg-brand-50 px-4 py-3">
          <span className="rounded-full bg-[#d9f0e6] px-[9px] py-1 text-[11px] font-semibold text-[#12a97e]">
            PROPOSED
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-6 p-6">
          <div className="rounded-[10px] border-[1.5px] border-dashed border-[#12a97e] bg-[#d9f0e6] px-4 py-3.5 shadow-[0px_2px_8px_0px_rgba(112,71,46,0.06)]">
            {drafting ? (
              <p className="flex items-center gap-2 text-[12.5px] font-semibold text-[#005945]">
                <Spinner /> Drafting with AI…
              </p>
            ) : draft.proposed_text ? (
              <p className="text-[12.5px] leading-[1.8] font-semibold text-[#005945]">
                “{draft.proposed_text}”
              </p>
            ) : (
              <p className="text-[12.5px] text-[#005945]">No proposed wording yet.</p>
            )}
          </div>
          <div>
            <Button
              unstyled
              disabledReason="Previews of unpublished changes come when changes can be applied to the website."
              className={pillGhost}
            >
              Preview
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

/** What has happened to the proposed change: accepted (not published yet), rejected, sent back, failed. */
function DraftState({ draft, people }: { draft: GrowthDraft; people: Map<string, User> }) {
  const by = draft.decided_by_user_id ? nameOf(people.get(draft.decided_by_user_id)) : null;
  const when = draft.decided_at ? formatDayMonthYear(draft.decided_at) : null;
  if (draft.status === "accepted") {
    return (
      <Note tone="success">
        Accepted{by ? ` by ${by}` : ""}
        {when ? ` on ${when}` : ""}. Changes aren't applied to the website yet, so the live page still shows
        the current wording.
      </Note>
    );
  }
  if (draft.status === "rejected") {
    return (
      <Note tone="muted">
        Rejected{by ? ` by ${by}` : ""}
        {when ? ` on ${when}` : ""}.{draft.revision_message ? ` “${draft.revision_message}”` : ""}
      </Note>
    );
  }
  if (draft.status === "revision_requested") {
    return (
      <Note tone="warning">
        <span className="font-semibold">Revision requested{by ? ` by ${by}` : ""}.</span>{" "}
        {draft.revision_reasons && draft.revision_reasons.length > 0 && (
          <span>{draft.revision_reasons.map(reasonLabel).join(", ")}. </span>
        )}
        {draft.revision_message && <span>“{draft.revision_message}”</span>}
      </Note>
    );
  }
  if (draft.status === "failed") {
    return (
      <Note tone="warning">
        The AI couldn't draft this change{draft.error_message ? `: ${draft.error_message}` : "."} Write it by
        hand below, or try again.
      </Note>
    );
  }
  return null;
}

function Note({ tone, children }: { tone: "success" | "warning" | "muted"; children: ReactNode }) {
  return (
    <p
      role="status"
      className={cx(
        "rounded-xl px-4 py-3 text-[13px] leading-[1.6]",
        tone === "success" && "bg-[#d9f0e6] text-[#005945]",
        tone === "warning" && "bg-[#fce7c6] text-[#7a4a00]",
        tone === "muted" && "bg-[#f3ebe7] text-[#5c5551]",
      )}
    >
      {children}
    </p>
  );
}

/** Write the change by hand, or have the AI draft it from the insight and the page's current wording. */
function DraftEditor({ task }: { task: GrowthTaskDetail }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const website = useGrowthWebsite();
  const ids = useId();
  const draft = task.draft;
  const fresh = !draft || ["accepted", "rejected", "superseded"].includes(draft.status);
  const [open, setOpen] = useState(
    !draft || draft.status === "failed" || draft.status === "revision_requested",
  );
  // null until someone types: the page then defaults to the business's website (once it has loaded)
  const [pageUrl, setPageUrl] = useState<string | null>(draft?.page_url ?? null);
  const [current, setCurrent] = useState(draft?.current_text ?? "");
  const [proposed, setProposed] = useState(fresh ? "" : (draft?.proposed_text ?? ""));
  const [instructions, setInstructions] = useState("");
  const page = pageUrl ?? website.data?.site?.url ?? "";
  const refresh = () => qc.invalidateQueries({ queryKey: growthKey(orgId) });

  const save = useMutation({
    mutationFn: () =>
      portal.saveDraft(orgId, task.id, {
        page_url: page.trim(),
        current_text: current.trim(),
        proposed_text: proposed.trim(),
        version: draft && draft.status === "draft" ? draft.version : undefined,
      }),
    onSuccess: () => {
      toast.show({ id: `draft-${task.id}`, tone: "success", title: "Proposed change saved" });
      setOpen(false);
    },
    onSettled: refresh,
  });
  const ai = useMutation({
    mutationFn: () =>
      portal.requestAiDraft(orgId, task.id, {
        page_url: page.trim(),
        current_text: current.trim(),
        instructions: instructions.trim() || undefined,
      }),
    onSuccess: () => {
      toast.show({
        id: `draft-${task.id}`,
        tone: "info",
        title: "Drafting with AI",
        body: "The proposed wording appears here in a moment.",
      });
      setOpen(false);
    },
    onSettled: refresh,
  });

  if (!open) {
    return (
      <div>
        <button type="button" onClick={() => setOpen(true)} className={pillGhost}>
          <Icon name="edit" size={16} className="mr-2" />
          Edit the proposed change
        </button>
      </div>
    );
  }
  const ready = page.trim() !== "" && current.trim() !== "";
  return (
    <section aria-labelledby={`${ids}-h`} className={cx(card, "flex flex-col gap-4 px-[22px] py-[18px]")}>
      <div className="flex flex-col gap-1">
        <h2 id={`${ids}-h`} className="text-[15px] font-semibold text-fg">
          Prepare the change
        </h2>
        <p className="text-[13px] text-fg-muted">
          Copy the sentence from the page as it reads now, then write the new wording or let the AI draft it.
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${ids}-url`} className={label}>
          Page
        </label>
        <input
          id={`${ids}-url`}
          type="url"
          value={page}
          onChange={(e) => setPageUrl(e.target.value)}
          placeholder="https://yourwebsite.com/page"
          className={control}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${ids}-current`} className={label}>
          Current wording
        </label>
        <textarea
          id={`${ids}-current`}
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          maxLength={2000}
          rows={2}
          className={cx(control, "resize-y")}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${ids}-proposed`} className={label}>
          Proposed wording
        </label>
        <textarea
          id={`${ids}-proposed`}
          value={proposed}
          onChange={(e) => setProposed(e.target.value)}
          maxLength={2000}
          rows={3}
          placeholder="Write it yourself, or leave it empty and use Draft with AI"
          className={cx(control, "resize-y")}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${ids}-instructions`} className="flex items-baseline gap-1">
          <span className={label}>Notes for the AI</span>
          <span className="text-xs text-fg-subtle">(optional)</span>
        </label>
        <input
          id={`${ids}-instructions`}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          maxLength={1000}
          placeholder="e.g. keep it under 25 words; mention the 1 November date"
          className={control}
        />
      </div>
      <ErrorAlert error={save.error ?? ai.error} />
      <div className="flex flex-wrap justify-end gap-2.5">
        {draft && (
          <button type="button" onClick={() => setOpen(false)} className={pillGhost}>
            Cancel
          </button>
        )}
        <Button
          unstyled
          onClick={() => ai.mutate()}
          loading={ai.isPending || save.isPending}
          disabledReason={ready ? undefined : NEEDS_PAGE}
          className={pillGhost}
        >
          {ai.isPending ? "Asking…" : "Draft with AI"}
        </Button>
        <Button
          unstyled
          onClick={() => save.mutate()}
          loading={save.isPending || ai.isPending}
          disabledReason={
            !ready ? NEEDS_PAGE : !proposed.trim() ? "Write the proposed change first." : undefined
          }
          className={cx(pillAction, "h-10 bg-brand-500 text-white hover:bg-brand-600")}
        >
          {save.isPending ? "Saving…" : "Save proposed change"}
        </Button>
      </div>
    </section>
  );
}

const NEEDS_PAGE = "Enter the page and its current wording first.";

/** "Needs approval from Riley J. or Dev P.": who can decide, for everyone who can't (QA-05). */
function ApprovalNote({ members }: { members: Member[] }) {
  const approvers = members
    .filter((m) => m.roles.some((r) => r.key === "org_owner" || r.key === "org_admin"))
    .map((m) => nameOf(m.user));
  const names =
    approvers.length > 1 ? `${approvers.slice(0, -1).join(", ")} or ${approvers.at(-1)}` : approvers[0];
  return (
    <Note tone="muted">
      Needs approval from {names ?? "an Owner or a Business Administrator"}, because approving changes to the
      website is part of the Owner and Business Administrator roles.
    </Note>
  );
}

/** The website's published versions (from the website runtime), newest first, with LIVE on the current one. */
/**
 * The website's versions on either side of this card's change: the change is the first version published after
 * the card was accepted (the website builder publishes it), and the one to go back to is the last before that.
 */
function changeVersions(task: GrowthTaskDetail, versions: GrowthWebsite["versions"]) {
  // approved when its draft was accepted (it went Live later, once published); without one, when it went Live
  const approved = task.draft?.status === "accepted" ? task.draft.decided_at : null;
  const when = approved ?? task.completed_at;
  const acceptedAt = task.status === "live" && when ? Date.parse(when) : null;
  if (acceptedAt === null) return { change: undefined, before: undefined };
  const ready = versions.filter((v) => v.status === "ready").sort((a, b) => a.number - b.number);
  return {
    change: ready.find((v) => Date.parse(v.created_at) >= acceptedAt),
    before: ready.filter((v) => Date.parse(v.created_at) < acceptedAt).at(-1),
  };
}

/**
 * Undo a Live change: the website goes back to the version that was live before it was published, and the card
 * is marked Rolled back (it stays Live). Approvers only; until the builder publishes the change there's nothing
 * to undo.
 */
function UndoChange({ task }: { task: GrowthTaskDetail }) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const website = useGrowthWebsite();
  const [confirming, setConfirming] = useState(false);
  const { change, before } = changeVersions(task, website.data?.versions ?? []);
  const undo = useMutation({
    mutationFn: () => portal.undoTask(orgId, task.id, task.version),
    onSuccess: () => {
      setConfirming(false);
      toast.show({
        id: `undo-${task.id}`,
        tone: "success",
        title: "Change undone",
        body: before ? `Your website is back to v${before.number}.` : "Your website is back as it was.",
      });
    },
    onError: (e) =>
      toast.show({
        id: `undo-${task.id}`,
        tone: "warning",
        title: "That didn't work",
        body: errorMessage(e),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: growthKey(orgId) }),
  });
  if (task.rolled_back) {
    return (
      <section className={cx(card, "px-[22px] py-[18px] text-[13.5px] text-fg-muted")}>
        <span className="font-semibold text-fg">Rolled back.</span> This change was undone: your website is
        back to the version from before it.
      </section>
    );
  }
  if (!change || !before) return null;
  return (
    <section className={cx(card, "flex flex-wrap items-center gap-4 px-[22px] py-[18px]")}>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h2 className="text-[15px] font-semibold text-fg">Published as v{change.number}</h2>
        <p className="text-[13px] text-fg-muted">
          Not right? Undo it and your website goes back to v{before.number}, as it was before this change.
        </p>
      </div>
      <Button
        unstyled
        requires="growth:approve"
        onClick={() => setConfirming(true)}
        loading={undo.isPending}
        className={cx(pillAction, "border-[1.5px] border-[#d4cac4] text-[#5c5551] hover:bg-muted")}
      >
        Undo this change
      </Button>
      {confirming && (
        <ConfirmUndo
          title={task.title}
          version={before.number}
          busy={undo.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => undo.mutate()}
        />
      )}
    </section>
  );
}

function ConfirmUndo({
  title,
  version,
  busy,
  onCancel,
  onConfirm,
}: {
  title: string;
  version: number;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const ids = useId();
  return (
    <Sheet
      onClose={onCancel}
      labelledBy={`${ids}-title`}
      className="max-w-[400px] rounded-[18px] shadow-[0_8px_24px_rgba(51,31,20,0.12)]"
    >
      <div className="flex flex-col gap-3.5 p-[22px]">
        <h2 id={`${ids}-title`} className="text-lg font-bold text-fg">
          Undo this change?
        </h2>
        <p className="text-[13.5px] font-semibold text-fg">{title}</p>
        <p className="text-[13.5px] leading-[1.6] text-[#5c5551]">
          Your website goes back to v{version}, the version live before this change. The task stays Live,
          marked Rolled back.
        </p>
        <div className="flex justify-end gap-2.5 pt-1">
          <button type="button" onClick={onCancel} className={ghostButton}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            aria-busy={busy || undefined}
            className={primaryButton}
            autoFocus
          >
            {busy ? "Undoing…" : "Undo change"}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

function VersionHistory({ task }: { task: GrowthTaskDetail }) {
  const website = useGrowthWebsite();
  const { change } = changeVersions(task, website.data?.versions ?? []);
  return (
    <section aria-labelledby="gi-versions" className={cx(card, "flex flex-col gap-3 px-[22px] py-[18px]")}>
      <h2 id="gi-versions" className="flex items-center gap-2 text-[15px] font-semibold text-fg">
        <Icon name="refresh-ccw" size={18} className="text-fg-muted" />
        Version history
      </h2>
      {website.isPending ? (
        <WidgetLoading label="Loading the website's versions" />
      ) : website.isError ? (
        <WidgetError error={website.error} onRetry={() => void website.refetch()} />
      ) : !website.data.available ? (
        <p className="py-2 text-[13px] text-fg-muted">We couldn't reach your website's history right now.</p>
      ) : !website.data.site ? (
        <p className="py-2 text-[13px] text-fg-muted">
          No website yet. Once you publish one, its versions show here.
        </p>
      ) : website.data.versions.length === 0 ? (
        <p className="py-2 text-[13px] text-fg-muted">{website.data.site.name} hasn't been published yet.</p>
      ) : (
        <ol className="flex flex-col divide-y divide-[#f3ebe7]">
          {website.data.versions.map((v) => (
            <li key={v.deployment_id} className="flex items-center gap-3 py-[9px]">
              <span className="w-[30px] shrink-0 text-[13px] font-semibold text-fg">v{v.number}</span>
              <span className="min-w-0 flex-1 text-[12.5px] text-[#5c5551]">{v.description}</span>
              {v.deployment_id === change?.deployment_id && (
                <span className="rounded-full bg-brand-100 px-[9px] py-[3px] text-[10.5px] font-semibold text-brand-700">
                  THIS CHANGE
                </span>
              )}
              <time dateTime={v.created_at} className="shrink-0 text-xs whitespace-nowrap text-fg-subtle">
                {timeAgo(v.created_at)}
              </time>
              {v.is_live && (
                <span className="rounded-full bg-[#d9f0e6] px-[9px] py-[3px] text-[10.5px] font-semibold text-[#12a97e]">
                  LIVE
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// ── the task itself ──────────────────────────────────────────────────────────

function TaskFacts({ task, people }: { task: GrowthTaskDetail; people: Map<string, User> }) {
  const phase = PHASES.find((p) => p.id === task.status);
  return (
    <section aria-label="Task details" className={cx(card, "flex flex-col gap-4 px-[22px] py-[18px]")}>
      {task.description && <p className="text-[14px] leading-[1.7] text-[#5c5551]">{task.description}</p>}
      <dl className="grid gap-x-6 gap-y-3 text-[13px] sm:grid-cols-2 lg:grid-cols-4">
        <Fact term="Assigned to">
          {task.assignee_user_ids.length > 0 ? (
            <ul aria-label="Assigned to" className="flex flex-col gap-1.5">
              {task.assignee_user_ids.map((id) => {
                const who = people.get(id);
                return (
                  <li key={id} className="flex items-center gap-2">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-fg-muted">
                      {initials(who ? who.display_name || who.email : "?")}
                    </span>
                    <span className="min-w-0 truncate">{nameOf(who)}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            "Nobody yet"
          )}
        </Fact>
        <Fact term="Due">{task.due_on ? formatDayMonthYear(task.due_on) : "No date"}</Fact>
        <Fact term="Priority">
          <span
            className={cx(
              "rounded-full px-2 py-[3px] text-[11px] font-semibold",
              PRIORITY[task.priority].tone,
            )}
          >
            {PRIORITY[task.priority].label}
          </span>
        </Fact>
        <Fact term="Status">
          <span className="flex items-center gap-2">
            <span aria-hidden className={cx("size-2 rounded-full", phase?.bar)} />
            {phase?.label ?? task.status}
          </span>
        </Fact>
      </dl>
      {task.attachment_count > 0 && <Files task={task} />}
    </section>
  );
}

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-fg-subtle">{term}</dt>
      <dd className="font-semibold text-fg">{children}</dd>
    </div>
  );
}

function Files({ task }: { task: GrowthTaskDetail }) {
  const { orgId } = useOrganization();
  const toast = useToast();
  const files = useQuery({
    queryKey: growthKey(orgId, "task", task.id, "files"),
    queryFn: () => portal.taskAttachments(orgId, task.id),
  });
  async function open(id: string) {
    try {
      const blob = await portal.taskAttachmentFile(orgId, task.id, id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.show({ tone: "warning", title: "Couldn't open the file", body: errorMessage(e) });
    }
  }
  return (
    <ul aria-label="Files" className="flex flex-col gap-2">
      {(files.data?.items ?? []).map((f) => (
        <li
          key={f.id}
          className="flex items-center gap-2.5 rounded-[10px] border border-[#e7ded9] px-3 py-2.5"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-500">
            <Icon name="file" size={16} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[13px] font-semibold text-fg">{f.filename}</span>
            <span className="text-[11.5px] text-fg-subtle">{formatBytes(f.size_bytes)}</span>
          </span>
          <button type="button" onClick={() => void open(f.id)} className={linkText}>
            Open
          </button>
        </li>
      ))}
    </ul>
  );
}

/** What happened to the card, newest first, in words. */
function Activity({ history, people }: { history: GrowthTaskChange[]; people: Map<string, User> }) {
  if (history.length === 0) return null;
  return (
    <section aria-labelledby="gi-activity" className={cx(card, "flex flex-col gap-3 px-[22px] py-[18px]")}>
      <h2 id="gi-activity" className="flex items-center gap-2 text-[15px] font-semibold text-fg">
        <Icon name="clock" size={18} className="text-fg-muted" />
        Activity
      </h2>
      <ol className="flex flex-col gap-2.5">
        {history.map((h, n) => (
          <li key={`${h.action}-${h.at}-${n}`} className="flex items-start gap-2.5">
            <span className="min-w-0 flex-1 text-[13px] text-fg-muted">
              <span className="font-semibold text-fg">
                {h.actor_user_id ? nameOf(people.get(h.actor_user_id)) : "Growth Intelligence"}
              </span>{" "}
              {describe(h, people)}
            </span>
            <time dateTime={h.at} className="shrink-0 text-xs whitespace-nowrap text-fg-subtle">
              {timeAgo(h.at)}
            </time>
          </li>
        ))}
      </ol>
    </section>
  );
}

const PHASE_WORD: Record<string, string> = Object.fromEntries(PHASES.map((p) => [p.id, p.label]));

/** A history record's people: a list of user ids, a single id, or nobody. */
function idList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  return value ? [String(value)] : [];
}

/** "Amara", "Amara and Liam", "Amara, Liam and Olivia". */
function listOf(names: string[]): string {
  return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

function describe(h: GrowthTaskChange, people: Map<string, User>): string {
  const after = h.after ?? {};
  const before = h.before ?? {};
  switch (h.action) {
    case "recommendation.created":
      return "created the task";
    case "recommendation.assigned": {
      // the list since several people could be on a card; one person before that (assignee_user_id)
      const was = idList(before.assignee_user_ids ?? before.assignee_user_id);
      const now = idList(after.assignee_user_ids ?? after.assignee_user_id);
      if (now.length === 0) return "unassigned it";
      const names = (ids: string[]) => listOf(ids.map((id) => nameOf(people.get(id))));
      const added = now.filter((id) => !was.includes(id));
      const removed = was.filter((id) => !now.includes(id));
      const parts = [
        ...(added.length ? [`assigned it to ${names(added)}`] : []),
        ...(removed.length ? [`took ${names(removed)} off it`] : []),
      ];
      return parts.join(" and ") || "changed who it's assigned to";
    }
    case "recommendation.updated":
      return "edited the task";
    case "recommendation.rolled_back":
      return after.version
        ? `undid the change: the website is back to v${String(after.version)}`
        : "undid the change";
    case "recommendation.approved":
      return "approved the change: it goes Live once the website is published";
    case "recommendation.moved": {
      if (after.published) return `published the change as v${String(after.published)}: it's Live`;
      const from = PHASE_WORD[String(before.status)] ?? String(before.status);
      const to = PHASE_WORD[String(after.status)] ?? String(after.status);
      const note = after.message ?? after.reason;
      return `moved it from ${from} to ${to}${note ? `: “${String(note)}”` : ""}`;
    }
    case "recommendation.draft_saved":
      return "saved the proposed change";
    case "recommendation.draft_requested":
      return "asked the AI to draft the change";
    case "recommendation.draft_prepared":
      return "drafted the proposed change";
    case "recommendation.draft_failed":
      return "couldn't draft the proposed change";
    case "recommendation.attachment_added":
      return `attached ${String(after.filename ?? "a file")}`;
    case "recommendation.attachment_removed":
      return `removed ${String(after.filename ?? "a file")}`;
    default:
      return h.action.replace(/^recommendation\./, "").replaceAll("_", " ");
  }
}
